import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { POST } from "../../utils/webService";
import {
  GET_WALLET_VALIDATION_RULES,
  WALLET_MIN_AMOUNT,
  WALLET_MAX_AMOUNT,
  WALLET_MULTIPLE,
  WALLET_TESTING_AMOUNT,
} from "../../utils/ApiUrls";

export interface WalletValidationRules {
  minAmount: number;
  maxAmount: number;
  multiple: number;
  testingAmount?: number; // Optional, for testing purposes
}

const DEFAULT_VALIDATION_RULES: WalletValidationRules = {
  minAmount: WALLET_MIN_AMOUNT,
  maxAmount: WALLET_MAX_AMOUNT,
  multiple: WALLET_MULTIPLE,
  testingAmount: WALLET_TESTING_AMOUNT,
};

interface WalletValidationState {
  rules: WalletValidationRules;
  loading: boolean;
  error: string | null;
}

const initialState: WalletValidationState = {
  rules: DEFAULT_VALIDATION_RULES,
  loading: false,
  error: null,
};

// Async thunk to fetch wallet validation rules from backend
export const fetchWalletValidationRules = createAsyncThunk(
  "walletValidation/fetchRules",
  async (_, thunkApi) => {
    try {
      const { data: response } = await POST(GET_WALLET_VALIDATION_RULES, {});
      if (response?.success) {
        return {
          minAmount:
            response?.data?.MIN_AMOUNT || DEFAULT_VALIDATION_RULES.minAmount,
          maxAmount:
            response?.data?.MAX_AMOUNT || DEFAULT_VALIDATION_RULES.maxAmount,
          multiple:
            response?.data?.MULTIPLE || DEFAULT_VALIDATION_RULES.multiple,
          testingAmount:
            response?.data?.TESTING_AMOUNT ||
            DEFAULT_VALIDATION_RULES.testingAmount,
        } as WalletValidationRules;
      } else {
        return thunkApi.rejectWithValue(
          response?.message || "Failed to fetch validation rules"
        );
      }
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Error fetching validation rules"
      );
    }
  }
);

export const walletValidationSlice = createSlice({
  name: "walletValidation",
  initialState,
  reducers: {
    setValidationRules: (
      state,
      action: PayloadAction<WalletValidationRules>
    ) => {
      state.rules = action.payload;
      state.error = null;
    },
    clearValidationRules: (state) => {
      state.rules = DEFAULT_VALIDATION_RULES;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWalletValidationRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletValidationRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload;
        state.error = null;
      })
      .addCase(fetchWalletValidationRules.rejected, (state, action) => {
        console.log("Failed to fetch wallet validation rules:", action.payload);

        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch validation rules";
        state.rules = DEFAULT_VALIDATION_RULES;
      });
  },
});

export const { setValidationRules, clearValidationRules, setError } =
  walletValidationSlice.actions;

export default walletValidationSlice.reducer;
